/**
 * UC Merced brand colors.
 *
 * Source: UC Merced brand site, Colors page (uc-merced.foleon.com
 * public-relations/ucmerced-brand/colors) — captured 2026-07-23 as S1
 * evidence (admin_tools/backend/data/onboarding/ucmerced/s1_captures/
 * brand_colors_foleon.html):
 *
 *   Bobcat Blue: #002856 (PMS 295, RGB 0 40 86)  — primary
 *   Bobcat Gold: #DAA900 (PMS 110, RGB 218 169 0) — accent
 *
 * The 50–900 shade ladders are HSL-based interpolations around the brand
 * colors (brand value at `700` and as DEFAULT), mirroring the SJSU
 * pattern: brand-aligned for UI use, not an officially published
 * extended palette.
 *
 * Logo policy mirrors UMD/SJSU: text-only branding (university name in
 * brand colors), no wordmark or Rufus-the-Bobcat asset embedded.
 */
import type { ThemeColors } from '../types';

export const ucmercedColors: ThemeColors = {
  // Primary — Bobcat Blue (PMS 295, #002856 = rgb(0, 40, 86))
  '--color-primary':     '0 40 86',
  // -strong: Bobcat Blue is very dark navy — white text passes easily
  // (≈15:1), so -strong is the base brand color (v8.13 token rules).
  '--color-primary-strong': '0 40 86',
  '--color-primary-50':  '240 247 255',
  '--color-primary-100': '214 233 255',
  '--color-primary-200': '173 212 255',
  '--color-primary-300': '112 180 255',
  '--color-primary-400': '41 140 255',
  '--color-primary-500': '0 105 224',
  '--color-primary-600': '0 72 153',
  '--color-primary-700': '0 40 86',
  '--color-primary-800': '0 30 64',
  '--color-primary-900': '0 20 43',

  // Accent — Bobcat Gold (PMS 110, #DAA900 = rgb(218, 169, 0))
  '--color-accent':     '218 169 0',
  '--color-accent-50':  '253 250 240',
  '--color-accent-100': '250 240 212',
  '--color-accent-200': '245 224 168',
  '--color-accent-300': '238 203 108',
  '--color-accent-400': '228 184 46',
  '--color-accent-500': '200 155 0',
  '--color-accent-600': '170 132 0',
  '--color-accent-700': '218 169 0',
  '--color-accent-800': '105 81 0',
  '--color-accent-900': '70 54 0',

  // Brand-mark faces (components/ui/BrandMark.tsx).
  //
  // Bobcat Blue and SJSU Blue are the SAME HUE — 212° and 209°, measured, not
  // estimated. The two schools are told apart by LIGHTNESS alone: 17% vs 32%.
  // Nothing else separates them, which makes the obvious move here a trap.
  //
  // That trap shipped once. Bobcat Blue is near-black, so a ramp descending
  // from it puts all three faces in the 8-17% band where the facets stop
  // reading. The first attempt lightened the front face to L36% to recover
  // them — which pushed UC Merced PAST SJSU (31.8%), erasing the only
  // distinguishing dimension and inverting it. On screen the two headers were
  // indistinguishable; that is what came back from staging review.
  //
  // So the brand value IS the front face, and subtle facets are the accepted
  // cost. Identity beats facet contrast on a 28px mark: the silhouette and the
  // hole carry the form, and every face still clears 12:1 on warm paper. The
  // resulting front-face gap vs SJSU is 14.9 points, against the 15 the brands
  // themselves differ by.
  //
  // Do NOT "fix" the flatness by lightening the front face — that is exactly
  // the regression above. If more separation is wanted, darken top/side.
  '--color-mark-front': '0 40 86',
  '--color-mark-top':   '0 28 62',
  '--color-mark-side':  '0 17 40',
  // Dark mode is re-derived, not scaled. On warm charcoal a 210° blue needs
  // L≈40% just to reach 3:1, so requiring that of all three faces would force
  // both schools into one narrow band and recreate the collision. The front
  // face stays strong (4.49:1) and carries identity; the side facet sits at
  // 1.83:1 and reads as shadow, which is what it is — SJSU's shipped dark ramp
  // is 2.20:1 for the same reason. Logos are WCAG contrast-exempt, and being
  // unable to tell two schools apart is the worse failure. Gap here: 19 points.
  '--color-mark-front-dm': '0 125 235',
  '--color-mark-top-dm':   '0 95 180',
  '--color-mark-side-dm':  '0 66 128',
};
